// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentPage, getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Chat from "@werner/App/Components/Chat/Chat";
import StyledOverlay from "@werner/App/Components/Common/StyledOverlay";
import XngUtilities from "@werner/App/Components/Common/XngUtilities";
import XngZone from "@werner/App/Components/Common/XngZone";
import ResetPasswordModal from "@werner/App/Components/Header/ResetPasswordModal";
import xHeaderUpdate from "@werner/App/Handlers/XNG/Header";
import useSize from "@werner/App/Hooks/useSize";
import { CustomAppState } from "@werner/App/Store/Reducers";
import StepperContextProvider, { StepperContext } from "@werner/App/Widgets/CheckoutStepper/StepperContext";
import { checkoutPages } from "@werner/XDL/constants";
import { color, layoutContainer, xScreen } from "@werner/XDL/Utilities";
import React, { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { hideAll } from "tippy.js";

const mapStateToProps = (state: CustomAppState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    // XNG-Changes
    isTabletScreen: state.xCommon.isTabletScreen,
    registerMenu: state.xHeader.registerMenu,
    currentLocation: getLocation(state),
    isSticky: state.xHeader.isSticky,
    currentShipToState: getCurrentShipToState(state),
    navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
    isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
    // XNG-Changes: Load logo image
    logoImage: getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Basic/Logo";
    })?.fields.logoImage,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
    loadCurrentShipTo,
    // XNG-Changes
    setNavDrawerIsOpen,
    xHeaderUpdate,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, true, true, true],
        background: "common.white",
        css: css`
            position: relative;
            z-index: ${getProp("theme.zIndex.header")};
            &&&& {
                overflow: visible;

                ${xScreen("lte", "md")} {
                    position: sticky;
                    top: 0;
                    box-shadow: 0 2px 20px 0 ${color("primary", 0.15)};
                }
            }
        `,
    },
    container: {
        css: css`
            background-color: ${getColor("primary.main")};
            display: flex;
            > div {
                ${layoutContainer}
            }
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
                img {
                    max-width: 200px;
                    width: 100%;
                }
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    isTabletScreen,
    registerMenu,
    currentLocation,
    isSticky,
    currentShipToState,
    loadCurrentShipTo,
    setNavDrawerIsOpen,
    navDrawerIsOpen,
    isCheckoutPage,
    xHeaderUpdate,
    // XNG-Changes: load logo image
    logoImage,
}: Props) => {
    const headerRef = useRef<HTMLDivElement>(null);
    const headerHeight = useSize(headerRef)?.height;

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler} aria-label="View My Dashboard">
                            {translate("View My Dashboard")}
                        </Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    useEffect(() => {
        if (headerHeight) {
            xHeaderUpdate({ key: "headerHeight", value: `${headerHeight}px` });
        }
    }, [headerHeight]);

    // XNG-Changes: Hide main navigation
    useEffect(() => {
        setTimeout(() => {
            hideAll();
        });
        if (navDrawerIsOpen) {
            setNavDrawerIsOpen({ navDrawerIsOpen: false });
            setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
        }
    }, [isSticky, currentLocation]);

    // XNG-Change
    useEffect(() => {
        if (session.isAuthenticated && !session.isGuest && !currentShipToState.value && !currentShipToState.isLoading) {
            loadCurrentShipTo();
        }
    }, []);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            <StyledWrapper
                ref={headerRef}
                css={css`
                    z-index: ${getProp("theme.zIndex.header")};
                    ${xScreen("lte", "md")} {
                        position: sticky;
                        top: 0;
                        box-shadow: 0 2px 20px 0 ${color("primary", 0.15)};
                    }
                    @media print {
                        position: static;
                        box-shadow: none;
                    }
                `}
            >
                <StepperContextProvider>
                    {/* XNG-Changes: add logo for print to be on top of the page */}
                    <StyledWrapper {...styles.websiteName} className="Logo">
                        <img src={logoImage} alt={translate("Werner Company Logo")} />
                    </StyledWrapper>
                    <Page as="header" isHeader={true} {...styles.page}>
                        <SkipNav
                            extendedStyles={styles.skipToContent}
                            text={translate("Skip to main content")}
                            destination={afterHeader}
                        />
                        <EagerLoadingLogoProvider value={true}>
                            <XngZone contentId={id} zoneName="GlobalSiteMessage" hint="Zone for Global Site Message" />
                            <Zone contentId={id} zoneName="SecondaryNavigation" fixed />
                            <StyledWrapper {...styles.container} id="main-navigation-container">
                                <Zone contentId={id} zoneName="MainNavigation" fixed />
                            </StyledWrapper>
                            <Zone contentId={id} zoneName="Breadcrumb" />
                        </EagerLoadingLogoProvider>
                        <span ref={afterHeader} tabIndex={-1} />
                    </Page>
                </StepperContextProvider>
            </StyledWrapper>
            <XngUtilities />
            <StyledOverlay />
            <Chat />
            {/* Xng-Change */}
            <ResetPasswordModal />
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
